import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage } from "@/app/api";
import { TextField } from "@/app/fields";
import { notify, type NotifySettings, type NotifySettingsInput, type NotifyTestResult } from "@/app/notify";
import { useSave } from "@/app/useSave";
import { Alert, Button, EmptyState, Field, Fieldset, Form, formatTime, Input, Pill } from "@/ui";

/** 密钥怎么处理：keep 不改（不传），set 换成新值，clear 清除（传 null，之后不再签名）；与通道凭证的约定一致（D43）。 */
type SecretMode = "keep" | "set" | "clear";

/** “设置”页签：webhook 地址、签名密钥与 source，下面是“测试发送”（docs/04 第 15 节）。 */
export function NotifySettingsPanel() {
  const q = useQuery({ queryKey: ["notify-settings"], queryFn: notify.settings });
  if (q.error) return <Alert tone="danger" title="加载失败">{q.error.message}</Alert>;
  if (!q.data) return <EmptyState title="加载中" />;
  return <SettingsForm settings={q.data} />;
}

/** 服务端的地址长度上限（internal/roles/admin/notify.go 的 maxNotifyURL），按 UTF-8 字节算。 */
const MAX_URL_BYTES = 2048;

/** http、https 且有主机，与服务端一致。new URL 会把 http:///x 当成主机 x，所以先要求 // 后紧跟主机。 */
function validUrl(u: string): boolean {
  if (!/^https?:\/\/[^/?#\s]/i.test(u)) return false;
  try {
    const x = new URL(u);
    return (x.protocol === "http:" || x.protocol === "https:") && x.host !== "";
  } catch {
    return false;
  }
}

/** 保存前的本地校验，与服务端同规则（地址留空或 http、https 的绝对地址且不超过 2048 字节，source 必填且不超过 64 个字符）。 */
function validate(url: string, source: string): { url?: string; source?: string } {
  return {
    url: url && new TextEncoder().encode(url).length > MAX_URL_BYTES ? "地址不超过 2048 字节" : url && !validUrl(url) ? "地址须是带主机的 http:// 或 https:// 地址" : undefined,
    source: !source ? "必填" : [...source].length > 64 ? "不超过 64 个字符" : undefined,
  };
}

function SettingsForm({ settings }: { settings: NotifySettings }) {
  const [url, setUrl] = useState(settings.url);
  const [source, setSource] = useState(settings.source);
  const [mode, setMode] = useState<SecretMode>("keep");
  const [secret, setSecret] = useState("");
  const [errors, setErrors] = useState<{ url?: string; source?: string }>({});
  const configured = settings.secret !== "";
  const dirty = url !== settings.url || source !== settings.source || mode !== "keep";
  const { save, saving, error } = useSave((body: NotifySettingsInput) => notify.saveSettings(body), {
    invalidate: ["notify-settings"], message: "已保存事件通知设置",
    onSaved: (s) => { setUrl(s.url); setSource(s.source); setMode("keep"); setSecret(""); },
  });
  const submit = () => {
    const u = url.trim(), s = source.trim();
    const errs = validate(u, s);
    setErrors(errs);
    if (errs.url || errs.source) return;
    // 留空与打码值都是不改；清除传 null
    const secretField = mode === "clear" ? { secret: null } : mode === "set" ? { secret } : {};
    save({ url: u, source: s, ...secretField });
  };
  return (
    <div className="ax-stack">
      <Fieldset title="Webhook">
        <Form>
          <TextField id="nt-url" label="地址" mono value={url} error={errors.url} placeholder="https://example.com/hook"
            hint="留空表示不通知，事件一律标为已跳过；允许 http（内网接收方）" onChange={setUrl} />
          <Form columns={2}>
            <TextField id="nt-source" label="source" required mono value={source} error={errors.source} hint="报文里的系统标识，接收方据此区分环境" onChange={setSource} />
            <Field label="签名密钥" htmlFor="nt-secret" hint={secretHint(configured, mode)}>
              <div className="ax-row">
                <Input id="nt-secret" mono autoComplete="off" value={secret} disabled={mode === "clear"} onChange={(e) => { setSecret(e.target.value); setMode(e.target.value ? "set" : "keep"); }}
                  placeholder={configured ? `${settings.secret}（已设置，留空表示不改）` : "不设置则不签名"} />
                {mode === "clear"
                  ? <Button variant="ghost" size="sm" onClick={() => setMode("keep")}>撤销清除</Button>
                  : configured && <Button variant="ghost" size="sm" className="is-danger" onClick={() => { setMode("clear"); setSecret(""); }}>清除密钥</Button>}
                {configured && mode === "keep" && <Pill tone="success">已设置</Pill>}
              </div>
            </Field>
          </Form>
          {error && <Alert tone="danger" title="保存失败">{error}</Alert>}
          <div className="ax-row">
            <Button variant="primary" disabled={saving || !dirty} onClick={submit}>保存</Button>
            {settings.updated_at && <span className="ax-secondary">上次保存 {formatTime(settings.updated_at)}</span>}
          </div>
        </Form>
      </Fieldset>
      <TestSend configured={!!settings.url} dirty={dirty} />
    </div>
  );
}

function secretHint(configured: boolean, mode: SecretMode) {
  if (mode === "clear") return "保存后清除密钥，之后的报文不再签名";
  return configured ? "输入新值替换；留空保持不变" : "设置后每个报文带签名";
}

/** 测试发送：用**已保存**的设置发一个 system.test 事件，同步显示结果；不进发送记录（docs/04 第 15 节）。接收方失败接口也回 200，看 ok。 */
function TestSend({ configured, dirty }: { configured: boolean; dirty: boolean }) {
  const [result, setResult] = useState<NotifyTestResult | null>(null);
  const run = useMutation({ mutationFn: notify.test, onMutate: () => setResult(null), onSuccess: setResult });
  return (
    <Fieldset title="测试发送">
      <p className="ax-secondary">用已保存的设置发一条测试事件（system.test），结果直接显示在这里，不进发送记录。改了设置请先保存。</p>
      {!configured ? <p className="ax-secondary">还没有保存 webhook 地址，无法测试。</p> : dirty && <p className="ax-secondary">有未保存的修改，测试仍用已保存的设置。</p>}
      <div className="ax-row"><Button disabled={!configured || run.isPending} onClick={() => run.mutate()}>{run.isPending ? "发送中…" : "测试发送"}</Button></div>
      {run.error && <Alert tone="danger" title="测试发送失败">{errorMessage(run.error)}</Alert>}
      {result && <TestResult r={result} />}
    </Fieldset>
  );
}

function TestResult({ r }: { r: NotifyTestResult }) {
  // 非 2xx 时 error 已是“HTTP 状态码 响应体开头”，不再重复响应码
  return r.ok
    ? <Alert tone="success" title="接收方已收到">响应码 {r.status_code}，耗时 {r.duration_ms} ms</Alert>
    : <Alert tone="danger" title="接收方没有收下">耗时 {r.duration_ms} ms；{r.error}</Alert>;
}
